/**
 * The counterparty portal.
 *
 * The one page somebody outside the office ever sees. They have no account and
 * will not get one — the link is the whole credential, and this shows exactly
 * one thing: what they were asked for, and what they already sent.
 *
 * What is absent is the design. No case name, no client, no certificate, no
 * opinion, no mention that a diligence is happening at all. The two sides of a
 * deal do not trust each other, and a page that hinted at any of it would make
 * the feature unusable for the reason it exists.
 */
import { useRef, useState } from 'react'

import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import CircularProgress from '@mui/material/CircularProgress'
import LinearProgress from '@mui/material/LinearProgress'
import Typography from '@mui/material/Typography'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { toast } from 'react-toastify'

import BlankLayout from '@layouts/blank-layout'
import { getSystemMode } from '@core/utils/server-helpers'
import Logo from '@components/layout/shared/logo'

import { useTRPC } from '@/lib/trpc'

export const Route = createFileRoute('/documents/$token')({
  // The layout needs the system's colour mode, exactly as the login screen does.
  loader: () => ({ systemMode: getSystemMode() }),
  component: CounterpartyPortal
})

/** Turns the picked file into what tRPC can carry. */
const toBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '')
    reader.onerror = () => reject(new Error('Não foi possível ler o arquivo.'))
    reader.readAsDataURL(file)
  })

function CounterpartyPortal() {
  const { token } = Route.useParams()
  const { systemMode } = Route.useLoaderData()
  const trpc = useTRPC()
  const queryClient = useQueryClient()

  const [sending, setSending] = useState<string | null>(null)
  const inputs = useRef<Record<string, HTMLInputElement | null>>({})

  const { data, isPending, isError } = useQuery({
    ...trpc.counterparty.documents.queryOptions({ token }),
    retry: false
  })

  const send = useMutation(
    trpc.counterparty.send.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: trpc.counterparty.documents.queryKey({ token }) })
        toast.success('Documento enviado. Obrigado!')
      },
      onError: error => toast.error(error.message),
      onSettled: () => setSending(null)
    })
  )

  const pick = async (itemId: string, file: File | null | undefined) => {
    if (!file) return

    setSending(itemId)

    try {
      send.mutate({
        token,
        itemId,
        file: { name: file.name, mediaType: file.type || 'application/octet-stream', base64: await toBase64(file) }
      })
    } catch {
      setSending(null)
      toast.error('Não foi possível ler o arquivo.')
    }
  }

  if (isPending) {
    return (
      <BlankLayout systemMode={systemMode}>
        <div className='flex min-bs-screen items-center justify-center'>
          <CircularProgress />
        </div>
      </BlankLayout>
    )
  }

  // A wrong link and an expired one say the same thing on purpose: the page
  // must not tell whoever holds it whether it was ever valid.
  if (isError || !data) {
    return (
      <BlankLayout systemMode={systemMode}>
        <div className='flex min-bs-screen items-center justify-center pli-6'>
          <Card className='is-full max-is-[480px]'>
            <CardContent className='flex flex-col gap-4 plb-8 pli-8'>
              <Logo />
              <Typography variant='h5'>Este link não está mais disponível</Typography>
              <Typography color='text.secondary'>
                Ele pode ter expirado ou sido substituído. Peça um novo a quem enviou a solicitação.
              </Typography>
            </CardContent>
          </Card>
        </div>
      </BlankLayout>
    )
  }

  const received = data.documents.filter(document => document.received).length
  const percent = data.documents.length ? Math.round((received / data.documents.length) * 100) : 0

  return (
    <BlankLayout systemMode={systemMode}>
      <div className='flex min-bs-screen justify-center plb-10 pli-6'>
        <div className='is-full max-is-[640px] flex flex-col gap-6'>
          <Logo />

          <div>
            <Typography variant='h4'>Olá, {data.recipientName}</Typography>
            <Typography color='text.secondary' className='mbs-1'>
              Precisamos dos documentos abaixo. Envie um de cada vez — pode voltar a esta página quando quiser para
              conferir o que já chegou.
            </Typography>
          </div>

          {data.complete ? (
            <Alert severity='success' icon={<i className='tabler-circle-check' />}>
              Recebemos tudo. Obrigado — não é preciso fazer mais nada.
            </Alert>
          ) : (
            <div className='flex flex-col gap-2'>
              <div className='flex items-center justify-between'>
                <Typography variant='body2' color='text.secondary'>
                  {received} de {data.documents.length} enviados
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  O link vale até {new Date(data.expiresAt).toLocaleDateString('pt-BR')}
                </Typography>
              </div>
              <LinearProgress variant='determinate' value={percent} className='bs-[6px] rounded-full' />
            </div>
          )}

          <div className='flex flex-col gap-3'>
            {data.documents.map(document => (
              <Card key={document.itemId} variant='outlined'>
                <CardContent className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
                  <div className='flex flex-col gap-1'>
                    <Typography className='font-medium'>{document.documentName}</Typography>
                    <Typography variant='body2' color='text.secondary'>
                      {document.reason}
                    </Typography>
                    {document.received && (
                      <Typography variant='caption' color='success.main' className='flex items-center gap-1'>
                        <i className='tabler-check text-base' />
                        {document.fileName} — recebido
                      </Typography>
                    )}
                  </div>

                  <div className='shrink-0'>
                    <input
                      hidden
                      type='file'
                      ref={element => {
                        inputs.current[document.itemId] = element
                      }}
                      onChange={event => void pick(document.itemId, event.target.files?.[0])}
                    />
                    <Button
                      variant={document.received ? 'tonal' : 'contained'}
                      color={document.received ? 'secondary' : 'primary'}
                      disabled={sending !== null || !data.open}
                      startIcon={
                        <i className={sending === document.itemId ? 'tabler-loader-2 animate-spin' : 'tabler-upload'} />
                      }
                      onClick={() => inputs.current[document.itemId]?.click()}
                    >
                      {sending === document.itemId ? 'Enviando…' : document.received ? 'Enviar outro' : 'Enviar'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <Typography variant='caption' color='text.disabled'>
            Se algum documento não existe ou não se aplica ao seu caso, avise quem pediu — é melhor do que deixar em
            branco.
          </Typography>
        </div>
      </div>
    </BlankLayout>
  )
}
